import { supabase } from "@/integrations/supabase/client";

type RecommendedSkill = {
    name: string;
    skill_type: string | null;
};

type SkillType = "technical" | "soft" | null;

type JobTitleSkill = {
    name: string;
    skill_type: SkillType;
};

type JobTitleSkillRow = {
    skills: JobTitleSkill | null;
};

const MAX_TECHNICAL_SKILLS = 10;
const MAX_SOFT_SKILLS = 10;
const inFlightRecommendationRequests = new Map<string, Promise<void>>();

function normalizeValue(value: string | null | undefined): string {
    return value?.trim().toLowerCase() ?? "";
}

export async function skillsRecommendations(jobTitle: string, currentSkills: string[] = []): Promise<RecommendedSkill[]> {
    // This function returns up to 20 unique skills total:
    // - 10 technical
    // - 10 soft
    // while excluding any skills already present on the user's resume.
    const normalizedTitle = normalizeValue(jobTitle);
    if (!normalizedTitle) return [];

    const currentSkillsSet = new Set(currentSkills.map(normalizeValue).filter(Boolean));

    const buildCategorizedSkills = (inputRows: JobTitleSkillRow[]) => {
        const seen = new Set<string>();
        const technical: RecommendedSkill[] = [];
        const soft: RecommendedSkill[] = [];

        for (const row of inputRows) {
            const skill = row.skills;
            if (!skill?.name) continue;

            const normalizedSkillName = normalizeValue(skill.name);
            if (!normalizedSkillName) continue;
            if (currentSkillsSet.has(normalizedSkillName)) continue;
            if (seen.has(normalizedSkillName)) continue;

            seen.add(normalizedSkillName);

            if (skill.skill_type === "technical" && technical.length < MAX_TECHNICAL_SKILLS) {
                technical.push({
                    name: skill.name,
                    skill_type: skill.skill_type,
                });
            } else if (skill.skill_type === "soft" && soft.length < MAX_SOFT_SKILLS) {
                soft.push({
                    name: skill.name,
                    skill_type: skill.skill_type,
                });
            }

            if (technical.length >= MAX_TECHNICAL_SKILLS && soft.length >= MAX_SOFT_SKILLS) {
                break;
            }
        }

        return {
            technical,
            soft,
            all: [...technical, ...soft],
        };
    };

    const fetchRows = async (): Promise<JobTitleSkillRow[]> => {
        const { data, error } = await (supabase as any)
            .from("job_title_skills")
            .select("skills(name, skill_type)")
            .eq("job_title", normalizedTitle);

        if (error) {
            console.error("Error fetching skills for job title:", error);
            return [];
        }

        return (data ?? []) as JobTitleSkillRow[];
    };

    let rows = await fetchRows();
    let categorized = buildCategorizedSkills(rows);

    // We need 10 technical + 10 soft after excluding resume skills.
    // If the DB does not have enough yet, call AI once per title and fetch again.
    if (categorized.technical.length < MAX_TECHNICAL_SKILLS || categorized.soft.length < MAX_SOFT_SKILLS) {
        await getRecommendedSkills(normalizedTitle);
        rows = await fetchRows();
        categorized = buildCategorizedSkills(rows);
    }

    console.log("Recommended skills for job title", jobTitle, ":", categorized.all);
    return categorized.all;
}

// If the DB is missing enough recommendations, call the AI model once per job title
// and let the edge function store the results for future requests.
async function getRecommendedSkills(jobTitle: string): Promise<void> {
    const normalizedTitle = normalizeValue(jobTitle);
    if (!normalizedTitle) return;

    const existingRequest = inFlightRecommendationRequests.get(normalizedTitle);
    if (existingRequest) {
        await existingRequest;
        return;
    }

    const request = (async () => {
        const response = await supabase.functions.invoke("recommend-skills", {
            body: { jobTitle: normalizedTitle },
        });

        if (response.error) {
            console.error("Error getting recommended skills from AI model:", response.error);
        }
    })();

    inFlightRecommendationRequests.set(normalizedTitle, request);

    try {
        await request;
    } finally {
        inFlightRecommendationRequests.delete(normalizedTitle);
    }
}
